import { CheckIcon, PlusIcon, TagIcon } from "@heroicons/react/24/outline";
import { TagIcon as TagSolidIcon } from "@heroicons/react/24/solid";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useCaseCategories } from "../../context/CaseCategoriesContext";
import Button from "../common/Button";
import Input from "../common/Input";
import Tooltip from "../common/Tooltip";

interface CaseCategoryMenuProps {
  caseId: string;
}

const MENU_WIDTH = 224; // matches w-56

// Toolbar tag button (sits left of the Favorites star). Click to open a
// dropdown for filing this case into categories — outline tag when the case is
// in none, filled blue when it belongs to at least one. The menu is portaled to
// <body> so it escapes the toolbar's stacking context (otherwise it renders
// behind the sidebar / chevron).
export default function CaseCategoryMenu({ caseId }: CaseCategoryMenuProps) {
  const {
    categories,
    addCaseToCategory,
    removeCaseFromCategory,
    createCategory,
    mutationVersion,
  } = useCaseCategories();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
    null
  );
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newName, setNewName] = useState("");

  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const createDialogRef = useRef<HTMLDialogElement>(null);

  // Load this case's current categories. Re-runs on mutationVersion so the tag
  // icon updates immediately when the case is filed into a category elsewhere
  // (e.g. dragged onto a sidebar category).
  useEffect(() => {
    if (!caseId) return;
    let cancelled = false;
    axios
      .get(`/api/cases/${caseId}/categories`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        setSelectedIds(res.data.categoryIds || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [caseId, mutationVersion]);

  // Close on outside click — but keep open while the create modal is up so the
  // user returns to the dropdown after creating.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (showCreateModal) return;
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showCreateModal]);

  // Keep the menu glued to the trigger while open (scroll / resize).
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 8,
        left: Math.max(8, rect.right - MENU_WIDTH), // right-aligned to trigger
      });
    };
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  // Focus the name field when the create modal opens.
  useEffect(() => {
    if (showCreateModal) {
      const t = setTimeout(
        () => createDialogRef.current?.querySelector("input")?.focus(),
        50
      );
      return () => clearTimeout(t);
    }
  }, [showCreateModal]);

  const isSelected = (id: string) => selectedIds.includes(id);

  const toggleCategory = (id: string) => {
    if (isSelected(id)) {
      setSelectedIds((prev) => prev.filter((x) => x !== id));
      removeCaseFromCategory(caseId, id);
    } else {
      setSelectedIds((prev) => [...prev, id]);
      addCaseToCategory(caseId, id);
    }
  };

  const openCreateModal = () => {
    setNewName("");
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewName("");
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    // "First" add = the case had no categories yet. In that case close the
    // dropdown afterwards; once the case is being edited, keep it open so more
    // can be added.
    const wasFirst = selectedIds.length === 0;
    const created = await createCategory(name);
    setNewName("");
    setShowCreateModal(false);
    if (created) {
      setSelectedIds((prev) => [...prev, created.id]);
      addCaseToCategory(caseId, created.id);
    }
    if (wasFirst) setOpen(false);
  };

  const hasCategories = selectedIds.length > 0;

  return (
    <div ref={triggerRef} className="inline-flex" data-tour="case-category-tag">
      <Tooltip
        placement="bottom"
        overlay={hasCategories ? "Edit categories" : "Add to category"}
      >
        <button
          // Suppress focus so the editor doesn't blur out from under the menu.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setOpen((o) => !o)}
          className="p-1 leading-none transition-opacity hover:opacity-60"
        >
          {hasCategories ? (
            <TagSolidIcon className="size-5 text-blue-500 dark:text-blue-300" />
          ) : (
            <TagIcon className="size-5 text-base-content/40" />
          )}
        </button>
      </Tooltip>

      {/* Category menu — portaled to <body> with fixed positioning so it sits
          above the sidebar/toolbar regardless of their stacking contexts. */}
      {open &&
        menuPos &&
        createPortal(
          <ul
            ref={menuRef}
            data-tour="category-tag-menu"
            className="menu fixed z-[1000] bg-base-100 rounded-lg w-56 p-2 shadow max-h-72 flex-nowrap overflow-y-auto"
            style={{
              top: menuPos.top,
              left: menuPos.left,
              animation: "dropdownPop 0.15s ease-out",
              transformOrigin: "top right",
            }}
          >
            {categories.length === 0 && (
              <li className="px-2 py-1 text-xs text-base-content/40 pointer-events-none">
                No categories yet
              </li>
            )}
            {categories.map((c) => (
              <li key={c.id}>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => toggleCategory(c.id)}
                  className="hover:bg-gray-100 dark:hover:bg-base-200"
                >
                  <span className="text-base-content/40 flex-shrink-0">#</span>
                  <span className="flex-1 min-w-0 truncate text-left">
                    {c.name}
                  </span>
                  {isSelected(c.id) && (
                    <CheckIcon className="size-4 text-blue-500 dark:text-blue-300 flex-shrink-0" />
                  )}
                </button>
              </li>
            ))}
            <li className="border-t border-base-200 mt-1 pt-1">
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={openCreateModal}
                className="hover:bg-gray-100 dark:hover:bg-base-200 text-blue-600 dark:text-blue-300"
              >
                <PlusIcon className="size-4" />
                New category
              </button>
            </li>
          </ul>,
          document.body
        )}

      {/* Create category modal — portaled to <body>; closing it does NOT close
          the dropdown. */}
      {createPortal(
        <dialog
          ref={createDialogRef}
          className={`modal ${showCreateModal ? "modal-open" : ""}`}
        >
          <div className="modal-box max-w-md">
            <h3 className="font-bold text-lg">New Category</h3>
            <p className="text-sm text-base-content/60 mt-2 mb-4">
              Organize your cases into a channel-style category.
            </p>
            <Input
              title="Category name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreate();
              }}
              placeholder="e.g. Traffic Stops"
              className="w-full !max-w-full"
            />
            <div className="modal-action">
              <Button variant="outline" onClick={closeCreateModal}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreate}
                disabled={!newName.trim()}
              >
                Create
              </Button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={closeCreateModal}>close</button>
          </form>
        </dialog>,
        document.body
      )}
    </div>
  );
}
